<template>
  <div class="floating-message-widget">
    <!-- Collapsed Message Bar -->
    <div 
      v-if="!isExpanded"
      :class="['message-bar', 'collapsed', { 'has-unread': hasUnreadMessages }]"
      @click="toggleWidget"
    >
      <div class="message-bar-content">
        <font-awesome-icon :icon="['fas', 'comment']" class="chat-icon" />
        <span class="message-text">Messages</span>
        <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }}</span>
      </div>
    </div>

    <!-- Expanded Message Widget -->
    <div v-if="isExpanded" class="message-widget expanded">
      <!-- Widget Header -->
      <div class="widget-header">
        <div class="header-title">
          <font-awesome-icon :icon="['fas', 'comment']" class="header-icon" />
          <span>Messages</span>
        </div>
        <div class="header-actions">
          <button @click="toggleWidget" class="close-btn" title="Close">
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>
      </div>

      <!-- Widget Navigation -->
      <div class="widget-nav">
        <button 
          @click="setActiveView('inbox')" 
          :class="['nav-btn', { active: activeView === 'inbox' }]"
        >
          <font-awesome-icon :icon="['fas', 'inbox']" />
          Inbox
          <span v-if="unreadCount > 0" class="nav-badge">{{ unreadCount }}</span>
        </button>
        <button 
          @click="setActiveView('compose')" 
          :class="['nav-btn', { active: activeView === 'compose' }]"
        >
          <font-awesome-icon :icon="['fas', 'pen']" />
          Compose
        </button>
      </div>

      <!-- Widget Content -->
      <div class="widget-content">
        <!-- Inbox View -->
        <div v-if="activeView === 'inbox'" class="inbox-view">
          <!-- Search Bar -->
          <div class="search-bar">
            <font-awesome-icon :icon="['fas', 'search']" class="search-icon" />
            <input 
              v-model="searchTerm"
              @input="searchMessages"
              type="text" 
              placeholder="Search messages..."
              class="search-input"
            />
          </div>

          <!-- Conversation Thread List -->
          <div class="message-list">
            <div 
              v-for="thread in filteredThreads" 
              :key="thread.id"
              @click="openThread(thread)"
              :class="['thread-item', { unread: thread.unreadCount > 0 }]"
            >
              <div class="thread-avatar">
                <font-awesome-icon :icon="['fas', 'user-md']" />
              </div>
              <div class="thread-content">
                <div class="thread-header">
                  <span class="participant-name">
                    <font-awesome-icon v-if="thread.isGroup" :icon="['fas', 'user-md']" class="group-icon" />
                    {{ thread.otherParticipant }}
                  </span>
                  <span class="thread-date">{{ formatDate(thread.lastMessage) }}</span>
                </div>
                <div v-if="thread.patientFocusName" class="thread-patient-focus">
                  [{{ thread.patientFocusName }}]
                </div>
                <div class="thread-preview-row">
                  <div
                    v-if="hasMessageAttachment(thread.lastMessage)"
                    class="thread-attachment-thumbs"
                  >
                    <message-attachment-preview
                      v-for="(att, attIdx) in normalizeMessageAttachments(thread.lastMessage).slice(0, 3)"
                      :key="'t-' + thread.id + '-' + attIdx"
                      :attachment="att"
                      variant="compact"
                    />
                  </div>
                  <div class="thread-preview">{{ threadPreviewText(thread) }}</div>
                </div>
                <div class="thread-info">
                  <span class="message-count">{{ thread.messages.length }} message{{ thread.messages.length !== 1 ? 's' : '' }}</span>
                </div>
              </div>
              <div v-if="thread.unreadCount > 0" class="unread-badge">{{ thread.unreadCount }}</div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="filteredThreads.length === 0" class="empty-state">
            <font-awesome-icon :icon="['fas', 'inbox']" class="empty-icon" />
            <p>No conversations found</p>
          </div>
        </div>

        <!-- Compose View -->
        <div v-if="activeView === 'compose'" class="compose-view">
          <!-- Contact Selection Interface -->
          <div v-if="showContactSelection" class="contact-selection-overlay">
            <contact-selection-list 
              @contacts-selected="onContactsSelected"
              @group-selected="onPatientGroupSelected"
              @cancel="showContactSelection = false"
            />
          </div>

          <form @submit.prevent="sendMessage" class="compose-form">
            <!-- Recipients -->
            <div class="form-group">
              <label>To:</label>
              <div class="recipient-selection">
                <button 
                  @click="openContactSelection" 
                  type="button"
                  class="add-recipients-btn"
                >
                  <font-awesome-icon :icon="['fas', 'plus-circle']" />
                  Add Recipients
                </button>
                
                <!-- Selected Recipients Display -->
                <div v-if="selectedRecipients.length > 0" class="selected-recipients">
                  <div class="recipients-header">
                    <span>{{ selectedRecipients.length }} recipient{{ selectedRecipients.length !== 1 ? 's' : '' }} selected</span>
                    <button @click="clearAllRecipients" type="button" class="clear-all-recipients">
                      Clear All
                    </button>
                  </div>
                  <div class="recipients-list">
                    <div 
                      v-for="(recipient, index) in selectedRecipients" 
                      :key="recipient.id || index"
                      class="recipient-tag"
                    >
                      <div class="recipient-avatar">
                        <font-awesome-icon :icon="getRecipientIcon(recipient)" />
                      </div>
                      <span class="recipient-name">{{ recipient.name }}</span>
                      <span class="recipient-role">{{ getRecipientRole(recipient) }}</span>
                      <button @click="removeRecipient(index)" type="button" class="remove-recipient">
                        <font-awesome-icon :icon="['fas', 'times']" />
                      </button>
                    </div>
                  </div>
                </div>
                
                <!-- Empty State -->
                <div v-if="selectedRecipients.length === 0" class="no-recipients">
                  <font-awesome-icon :icon="['fas', 'user-md']" class="no-recipients-icon" />
                  <span>No recipients selected</span>
                </div>
              </div>
            </div>

            <!-- Group Messaging Options -->
            <div v-if="selectedRecipients.length > 1" class="form-group">
              <div class="messaging-options">
                <label class="checkbox-label">
                  <input 
                    v-model="sendSeparately" 
                    type="checkbox"
                    class="messaging-checkbox"
                  />
                  <span class="checkmark"></span>
                  <span class="checkbox-text">Send separately to each recipient</span>
                </label>
                <div class="option-description">
                  <span v-if="sendSeparately" class="option-desc">
                    <font-awesome-icon :icon="['fas', 'user']" class="desc-icon" />
                    Creates individual conversations with each recipient
                  </span>
                  <span v-else class="option-desc">
                    <font-awesome-icon :icon="['fas', 'user-md']" class="desc-icon" />
                    Creates a group chat where everyone can reply to each other
                  </span>
                </div>
              </div>
            </div>

            <div v-if="notifyAvailable" class="form-group">
              <div class="messaging-options">
                <label class="checkbox-label">
                  <input
                    v-model="notifyRecipients"
                    type="checkbox"
                    class="messaging-checkbox"
                  />
                  <span class="checkmark"></span>
                  <span class="checkbox-text">Notify recipients (email / SMS / phone)</span>
                </label>
                <div class="option-description">
                  <span class="option-desc">
                    Sends an alert via each recipient&rsquo;s preferred communication. You are not notified.
                  </span>
                </div>
                <button
                  v-if="notifyRecipients"
                  type="button"
                  class="select-channels-btn"
                  @click="openNotifyChannelsModal"
                >
                  Select channels
                </button>
              </div>
            </div>

            <!-- Optional patient focus (1:1 / send-separately only) -->
            <!-- Pick a shared patient so this chat stays tied to them in the inbox. -->
            <div v-if="canSelectPatientFocus" class="form-group">
              <label>About patient <span class="optional-label">(optional)</span>:</label>
              <select
                v-model="selectedPatientFocusId"
                class="form-input patient-focus-select"
                :disabled="sharedPatientsLoading"
              >
                <option value="">None</option>
                <option
                  v-for="patient in sharedPatients"
                  :key="patient.id"
                  :value="patient.id"
                >
                  {{ patient.name }}
                </option>
              </select>
              <div v-if="sharedPatientsLoading" class="patient-focus-hint">Loading shared patients...</div>
              <div v-else-if="sharedPatients.length === 0" class="patient-focus-hint">
                No shared patients with selected recipient(s).
              </div>
            </div>

            <!-- Subject -->
            <div class="form-group">
              <label>Subject:</label>
              <div class="subject-row">
                <span v-if="selectedPatientFocusName" class="patient-focus-prefix">
                  [{{ selectedPatientFocusName }}]
                </span>
                <input 
                  v-model="newMessage.subject" 
                  type="text" 
                  placeholder="Message subject..."
                  class="form-input subject-input"
                  required
                />
              </div>
            </div>

            <!-- Message -->
            <div class="form-group">
              <label>Message:</label>
              <textarea 
                v-model="newMessage.message" 
                placeholder="Type your message..."
                class="form-textarea"
                rows="6"
              ></textarea>
            </div>

            <!-- Attachment (up to 5) -->
            <div class="form-group attachment-form-group">
              <label>Attachments <span class="optional-label">(optional, max {{ maxMessageAttachments }})</span>:</label>
              <div class="attachment-compose-row">
                <input
                  ref="composeAttachmentInput"
                  type="file"
                  class="hidden-file-input"
                  :accept="messageAttachmentAccept"
                  multiple
                  @change="onComposeAttachmentSelected"
                />
                <button
                  type="button"
                  class="attach-btn"
                  @click="triggerComposeAttachmentPicker"
                  :disabled="composeAttachmentFiles.length >= maxMessageAttachments"
                >
                  <font-awesome-icon :icon="['fas', 'file']" />
                  Add files
                </button>
                <button
                  v-if="composeAttachmentFiles.length"
                  type="button"
                  class="clear-attach-btn"
                  @click="clearComposeAttachment"
                >
                  Clear all
                </button>
              </div>
              <div v-if="composeAttachmentFiles.length" class="attachment-compose-list">
                <div
                  v-for="(file, fileIdx) in composeAttachmentFiles"
                  :key="'compose-att-' + fileIdx + '-' + file.name"
                  class="attachment-compose-item"
                >
                  <message-attachment-preview
                    :local-file="file"
                    :upload="videoUpload(file)"
                    variant="compose"
                    @upload-action="onVideoUploadAction(file, $event)"
                  />
                  <button
                    type="button"
                    class="clear-attach-btn"
                    @click="removeComposeAttachmentAt(fileIdx)"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="form-actions">
              <button type="submit" class="send-btn" :disabled="!canSendMessage">
                <font-awesome-icon :icon="['fas', 'plane-departure']" />
                Send Message
              </button>
              <button @click="clearMessage" type="button" class="clear-btn">
                Clear
              </button>
            </div>
          </form>
        </div>

        <!-- Message Detail View -->
        <div v-if="activeView === 'detail'" class="message-detail-view">
          <div class="detail-header">
            <button @click="backToInbox" class="back-btn">
              <font-awesome-icon :icon="['fas', 'angle-left']" />
              Back to Inbox
            </button>
            <button @click="replyToMessage" class="reply-btn">
              <font-awesome-icon :icon="['fas', 'reply']" />
              Reply
            </button>
          </div>

          <div v-if="selectedMessage" class="message-detail">
            <div class="detail-info">
              <h3>{{ selectedMessage.subject }}</h3>
              <div class="message-meta">
                <span><strong>From:</strong> {{ selectedMessage.pt_name }}</span>
                <span><strong>Date:</strong> {{ formatDate(selectedMessage) }}</span>
              </div>
            </div>
            
            <div class="message-body">
              {{ selectedMessage.message }}
            </div>

            <div
              v-if="hasMessageAttachment(selectedMessage)"
              class="message-attachments-list"
            >
              <span
                v-for="(att, attIdx) in normalizeMessageAttachments(selectedMessage)"
                :key="'detail-att-' + attIdx"
                class="attachment-with-actions"
              >
                <message-attachment-preview :attachment="att" variant="bubble" />
                <button
                  v-if="canRemoveAttachment(selectedMessage, att)"
                  type="button"
                  class="attachment-remove"
                  data-test="attachment-remove"
                  @click.stop="openRemoveAttachment(selectedMessage, att)"
                >Remove</button>
              </span>
            </div>

            <!-- Reply Form (if replying) -->
            <div v-if="isReplying" class="reply-form">
              <h4>Reply</h4>
              <form @submit.prevent="sendReply" class="compose-form">
                <div class="form-group">
                  <textarea 
                    v-model="replyMessage" 
                    placeholder="Type your reply..."
                    class="form-textarea"
                    rows="4"
                  ></textarea>
                </div>
                <div class="form-group attachment-form-group">
                  <div class="attachment-compose-row">
                    <input
                      ref="replyAttachmentInput"
                      type="file"
                      class="hidden-file-input"
                      :accept="messageAttachmentAccept"
                      multiple
                      @change="onReplyAttachmentSelected"
                    />
                    <button
                      type="button"
                      class="attach-btn"
                      @click="triggerReplyAttachmentPicker"
                      :disabled="replyAttachmentFiles.length >= maxMessageAttachments"
                    >
                      <font-awesome-icon :icon="['fas', 'file']" />
                      Attach files
                    </button>
                    <button
                      v-if="replyAttachmentFiles.length"
                      type="button"
                      class="clear-attach-btn"
                      @click="clearReplyAttachment"
                    >
                      Clear all
                    </button>
                  </div>
                  <div v-if="replyAttachmentFiles.length" class="attachment-compose-list">
                    <div
                      v-for="(file, fileIdx) in replyAttachmentFiles"
                      :key="'reply-att-' + fileIdx + '-' + file.name"
                      class="attachment-compose-item"
                    >
                      <message-attachment-preview
                        :local-file="file"
                        :upload="videoUpload(file)"
                        variant="compose"
                        @upload-action="onVideoUploadAction(file, $event)"
                      />
                      <button
                        type="button"
                        class="clear-attach-btn"
                        @click="removeReplyAttachmentAt(fileIdx)"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
                
                <div v-if="notifyAvailable" class="form-group">
                  <label class="checkbox-label">
                    <input
                      v-model="notifyRecipients"
                      type="checkbox"
                      class="messaging-checkbox"
                    />
                    <span class="checkmark"></span>
                    <span class="checkbox-text">Notify recipients (email / SMS / phone)</span>
                  </label>
                </div>

                <div class="form-actions">
                  <button type="submit" class="send-btn" :disabled="!canSendReply">
                    <font-awesome-icon :icon="['fas', 'plane-departure']" />
                    Send Reply
                  </button>
                  <button @click="cancelReply" type="button" class="cancel-btn">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <!-- Thread Conversation View -->
        <div v-if="activeView === 'thread'" class="thread-view">
          <div class="thread-header">
            <button @click="backToInbox" class="back-btn">
              <font-awesome-icon :icon="['fas', 'angle-left']" />
              Back to Conversations
            </button>
            <div class="thread-participant">
              <div class="participant-header">
                <font-awesome-icon v-if="selectedThread.isGroup" :icon="['fas', 'user-md']" class="group-icon" />
                <font-awesome-icon v-else :icon="['fas', 'user-md']" />
                <span class="participant-name">{{ selectedThread.otherParticipant }}</span>
              </div>
              <div v-if="selectedThread.patientFocusName" class="thread-patient-focus-header">
                About [{{ selectedThread.patientFocusName }}]
              </div>
              <!-- Group Participants List -->
              <div v-if="selectedThread.isGroup && selectedThread.otherParticipantNames" class="group-participants">
                <div class="participants-toggle" @click="showParticipants = !showParticipants">
                  <span class="participants-count">{{ selectedThread.otherParticipantNames.length + 1 }} members</span>
                  <font-awesome-icon :icon="['fas', showParticipants ? 'chevron-up' : 'chevron-down']" />
                </div>
                <div v-if="showParticipants" class="participants-list">
                  <!-- Show current user first -->
                  <div class="participant-item current-user">
                    <font-awesome-icon :icon="['fas', 'user-md']" class="participant-icon current" />
                    <span class="participant-member-name">You</span>
                  </div>
                  <!-- Show other participants -->
                  <div v-for="(name, index) in selectedThread.otherParticipantNames" :key="index" class="participant-item">
                    <font-awesome-icon :icon="['fas', 'user-md']" class="participant-icon" />
                    <span class="participant-member-name">{{ name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="selectedThread" class="chat-container">
            <!-- Chat Messages -->
            <div class="chat-messages" ref="chatMessages">
              <div 
                v-for="message in sortedThreadMessages" 
                :key="message.pd"
                :class="['chat-message', {
                  'own-message': message.sender_id == messagingIdentity.subAccountNumber,
                  'other-message': message.sender_id != messagingIdentity.subAccountNumber
                }]"
              >
                <div class="message-bubble">
                  <!-- Show sender name for group messages if not sent by current user -->
                  <div v-if="selectedThread.isGroup && message.sender_id != messagingIdentity.subAccountNumber" class="message-sender">
                    {{ getSenderName(message) }}
                  </div>
                  <div v-if="message.subject && message.subject !== 'Re:'" class="message-subject">
                    {{ message.subject }}
                  </div>
                  <div v-if="message.message && message.message !== '(Attachment)'" class="message-text">
                    {{ message.message }}
                  </div>
                  <div
                    v-if="hasMessageAttachment(message)"
                    class="message-attachments-list"
                  >
                    <span
                      v-for="(att, attIdx) in normalizeMessageAttachments(message)"
                      :key="'bubble-att-' + (message.pd || '') + '-' + attIdx"
                      class="attachment-with-actions"
                    >
                      <message-attachment-preview :attachment="att" variant="bubble" />
                      <button
                        v-if="canRemoveAttachment(message, att)"
                        type="button"
                        class="attachment-remove"
                        data-test="attachment-remove"
                        @click.stop="openRemoveAttachment(message, att)"
                      >Remove</button>
                    </span>
                  </div>
                  <div class="message-timestamp">{{ formatDate(message) }}</div>
                </div>
              </div>
            </div>

            <!-- Quick Reply -->
            <div class="quick-reply">
              <form @submit.prevent="sendQuickReply" class="reply-form">
                <div class="reply-input-container">
                  <input
                    ref="quickReplyAttachmentInput"
                    type="file"
                    class="hidden-file-input"
                    :accept="messageAttachmentAccept"
                    multiple
                    @change="onQuickReplyAttachmentSelected"
                  />
                  <button
                    type="button"
                    class="quick-attach-btn"
                    @click="triggerQuickReplyAttachmentPicker"
                    title="Attach files"
                    :disabled="quickReplyAttachmentFiles.length >= maxMessageAttachments"
                  >
                    <font-awesome-icon :icon="['fas', 'file']" />
                  </button>
                  <div class="quick-reply-main">
                    <div v-if="notifyAvailable" class="quick-reply-notify-row">
                      <label class="checkbox-label quick-reply-notify">
                        <input
                          v-model="notifyRecipients"
                          type="checkbox"
                          class="messaging-checkbox"
                        />
                        <span class="checkmark"></span>
                        <span class="checkbox-text">Notify recipients</span>
                      </label>
                      <button
                        v-if="notifyRecipients"
                        type="button"
                        class="select-channels-btn select-channels-btn--compact"
                        @click="openNotifyChannelsModal"
                      >
                        Select channels
                      </button>
                    </div>
                    <textarea 
                      v-model="quickReplyMessage" 
                      placeholder="Type your message..."
                      class="quick-reply-textarea"
                      rows="2"
                      @keydown.enter.exact.prevent="sendQuickReply"
                      @keydown.shift.enter.prevent="quickReplyMessage += '\n'"
                    ></textarea>
                  </div>
                  <button type="submit" class="quick-send-btn" :disabled="!canSendQuickReply">
                    <font-awesome-icon :icon="['fas', 'plane-departure']" />
                  </button>
                </div>
                <div v-if="quickReplyAttachmentFiles.length" class="quick-reply-attachment">
                  <div
                    v-for="(file, fileIdx) in quickReplyAttachmentFiles"
                    :key="'qr-att-' + fileIdx + '-' + file.name"
                    class="attachment-compose-item"
                  >
                    <message-attachment-preview
                      :local-file="file"
                      :upload="videoUpload(file)"
                      variant="compose"
                      @upload-action="onVideoUploadAction(file, $event)"
                    />
                    <button
                      type="button"
                      class="clear-attach-btn"
                      @click="removeQuickReplyAttachmentAt(fileIdx)"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <notify-channels-modal
        v-if="showNotifyChannelsModal"
        :rows="notifyChannelModalRows"
        :draft="notifyChannelDraft"
        :loading="notifyChannelsLoading"
        @cancel="cancelNotifyChannels"
        @save="saveNotifyChannels"
        @update-draft="onNotifyChannelDraftUpdate"
      />
    </div>
    <!-- One confirmation per widget for removing a sent attachment. -->
    <remove-file-dialog
      :show="!!removeAttachmentTarget"
      :file-name="removeAttachmentTarget ? removeAttachmentTarget.name : ''"
      :busy="removeAttachmentBusy"
      :error-text="removeAttachmentError"
      @confirm="confirmRemoveAttachment"
      @cancel="cancelRemoveAttachment"
    />
  </div>
</template>

<script>
import { CURR_API } from "@/constants/apis";
// The server's own words when it refused for a reason it named - an upload
// paused for the migration, a file over the size limit, a rate-limited login
// code - instead of a generic retry message that would be wrong for all three.
import { refusalMessage } from "@/api/refusals";
import axios from "axios";
import { mapGetters } from "vuex";
import ContactSelectionList from "./ContactSelectionList.vue";
import MessageAttachmentPreview from "./MessageAttachmentPreview.vue";
import RemoveFileDialog from "@/components/includes/RemoveFileDialog.vue";
import NotifyChannelsModal from "./NotifyChannelsModal.vue";
import messageAttachmentMixin from "./messageAttachmentMixin.js";
import notifyChannelsComposeMixin from "./notifyChannelsComposeMixin.js";
import { usePrivilegesStore } from "@/stores/privileges";
import { useSessionSelfStore } from "@/stores/sessionSelf";
import { PRIVILEGES } from "@/constants/privileges";
import { assertPrivilegeOrAlert } from "@/utils/privilegeGate";
import { messagingIdentityFrom } from "@/utils/sessionIdentity";
import { formatMessageWhen } from "@/utils/messageTimestamp";

export default {
  name: "FloatingMessageWidget",
  mixins: [messageAttachmentMixin, notifyChannelsComposeMixin],
  components: {
    ContactSelectionList,
    MessageAttachmentPreview,
    RemoveFileDialog,
    NotifyChannelsModal,
  },
  data() {
    return {
      isExpanded: false,
      activeView: 'inbox', // 'inbox', 'compose', 'detail'
      
      // Messages
      messages: [],
      filteredMessages: [],
      conversationThreads: [], // Grouped conversation threads
      filteredThreads: [], // Filtered conversation threads
      selectedThread: null, // Selected conversation thread
      selectedMessage: null,
      searchTerm: '',
      
      // Compose
      newMessage: {
        searchTerm: '',
        subject: '',
        message: ''
      },
      selectedRecipients: [],
      searchResults: [],
      showContactSelection: false,
      sendSeparately: false, // Default to current behavior
      notifyRecipients: false, // Opt-in preferred-comm notify for recipients
      // Optional "this chat is about patient X" picker state (1:1 only).
      sharedPatients: [],
      sharedPatientsLoading: false,
      selectedPatientFocusId: '',
      
      // Reply
      isReplying: false,
      replyMessage: '',
      quickReplyMessage: '', // For thread quick reply
      showParticipants: false,
      
      // Contact search
      searchTimeout: null
    };
  },

  watch: {
    selectedRecipients: {
      handler() {
        this.refreshSharedPatients();
      },
      deep: true,
    },
    sendSeparately() {
      this.refreshSharedPatients();
    },
  },
  
  computed: {
    ...mapGetters("login", {
      allData: "getAllData",
    }),
    
    unreadCount() {
      // Calculate unread count from conversation threads instead of raw messages
      return this.conversationThreads.reduce((total, thread) => total + thread.unreadCount, 0);
    },
    
    hasUnreadMessages() {
      return this.unreadCount > 0;
    },

    canSelectPatientFocus() {
      // 1:1 / send-separately only; skip when a recipient is already a patient.
      if (!this.selectedRecipients.length) return false;
      if (this.selectedRecipients.length > 1 && !this.sendSeparately) return false;
      const hasPatientRecipient = this.selectedRecipients.some((r) => {
        const t = String(r.accountType || '').toLowerCase();
        return t === 'patient' || t === 'patients';
      });
      return !hasPatientRecipient;
    },

    selectedPatientFocusName() {
      if (!this.selectedPatientFocusId) return '';
      const match = this.sharedPatients.find((p) => String(p.id) === String(this.selectedPatientFocusId));
      return match ? match.name : '';
    },

    // Subject as stored/sent: "[Patient Name] typed subject"
    composedSubject() {
      const body = (this.newMessage.subject || '').trim();
      if (!this.selectedPatientFocusName) return body;
      return `[${this.selectedPatientFocusName}] ${body}`.trim();
    },
    
    // Require subject + (text body or at least one attachment).
    canSendMessage() {
      const hasBody = !!this.newMessage.message.trim() || this.composeAttachmentFiles.length > 0;
      return this.selectedRecipients.length > 0 &&
             !!this.newMessage.subject.trim() &&
             hasBody &&
             !this.uploadsBusy('compose');
    },

    // A video that is still uploading (or failed) holds the send of its own box.
    canSendQuickReply() {
      return (!!this.quickReplyMessage.trim() || this.quickReplyAttachmentFiles.length > 0) &&
             !this.uploadsBusy('quickReply');
    },

    canSendReply() {
      return (!!this.replyMessage.trim() || this.replyAttachmentFiles.length > 0) &&
             !this.uploadsBusy('reply');
    },
    
    sortedThreadMessages() {
      if (!this.selectedThread || !this.selectedThread.messages) return [];
      
      // Sort messages chronologically (oldest first) for chat view using full timestamp
      return [...this.selectedThread.messages].sort((a, b) => {
        const dateA = new Date(a.msgTimestamp || a.msgDate);
        const dateB = new Date(b.msgTimestamp || b.msgDate);
        return dateA - dateB; // Oldest first
      });
    },
    canSendInternalMessages() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.SEND_INTERNAL_MESSAGES);
    },

    messagingIdentity() {
      return messagingIdentityFrom(
        this.allData,
        useSessionSelfStore().profile
      );
    }
  },
  
  async mounted() {
    await this.fetchMessages();
    // Check for new messages every 30 seconds
    this.messageInterval = setInterval(this.fetchMessages, 30000);
  },
  
  beforeUnmount() {
    if (this.messageInterval) {
      clearInterval(this.messageInterval);
    }
  },
  
  methods: {
    // Widget Controls
    toggleWidget() {
      const willExpand = !this.isExpanded;
      if (
        willExpand &&
        !assertPrivilegeOrAlert(
          PRIVILEGES.SEND_INTERNAL_MESSAGES,
          "You do not have permission to use internal messages."
        )
      ) {
        return;
      }
      this.isExpanded = willExpand;
      if (this.isExpanded) {
        this.setActiveView('inbox');
      }
    },
    
    minimizeWidget() {
      this.isExpanded = false;
    },
    
    closeWidget() {
      this.isExpanded = false;
    },
    
    setActiveView(view) {
      if (this.viewChangeBlocked()) return;
      if (
        view === "compose" &&
        !assertPrivilegeOrAlert(
          PRIVILEGES.SEND_INTERNAL_MESSAGES,
          "You do not have permission to compose internal messages."
        )
      ) {
        return;
      }
      this.activeView = view;
      if (view === 'compose') {
        this.clearMessage();
      }
    },
    
    // Message Management
    async fetchMessages() {
      try {
        // Fetching is allowed regardless of UI privileges; the widget still blocks usage.
        // Guard only on identity availability (common on first mount before session loads).
        if (!this.messagingIdentity.accountNumber || !this.messagingIdentity.subAccountNumber) {
          return;
        }
        const content = {
          Messages: {
            API_Meth: "FetchAllMessagesByIDS",
            accountnumber: this.messagingIdentity.accountNumber,
            subaccountnumber: this.messagingIdentity.subAccountNumber,
          },
        };
        
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        
        if (response.data && response.data.records) {
          // Remove duplicates based on message ID (pd field)
          const uniqueMessages = response.data.records.filter((message, index, self) => 
            index === self.findIndex(m => m.pd === message.pd)
          );
          
          // Sort messages chronologically (newest first) using full timestamp
          this.messages = uniqueMessages.sort((a, b) => {
            const dateA = new Date(a.msgTimestamp || a.msgDate);
            const dateB = new Date(b.msgTimestamp || b.msgDate);
            return dateB - dateA; // Newest first
          });
          
          // console.log(`Original messages: ${response.data.records.length}, After deduplication: ${this.messages.length}`);
          
          // Debug: Check message statuses
          const statusCounts = {};
          this.messages.forEach(msg => {
            statusCounts[msg.msgStatus] = (statusCounts[msg.msgStatus] || 0) + 1;
          });
          // console.log('Message status counts:', statusCounts);
          
          // Group messages into conversation threads
          this.groupMessagesByThread();
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
    },
    
    groupMessagesByThread() {
      // Group messages by conversation participants (individual and group)
      const threads = new Map();
      const currentUser = this.messagingIdentity.subAccountNumber; // Current messaging subaccount number
      
      // console.log(`Current user ID: ${currentUser}`);
      
      this.messages.forEach(message => {
        // console.log(`Processing message: sender=${message.sender_id}, recipient=${message.recipient_id}, is_group=${message.is_group_message}, group_id=${message.group_id}`);
        
        // Handle group messages
        if (message.is_group_message == 1 && message.group_id) {
          const threadKey = `group_${message.group_id}`;
          
          if (!threads.has(threadKey)) {
            // Parse participants from group_participants field
            const participantIds = message.group_participants ? message.group_participants.split(',').map(id => id.trim()) : [];

            // Keep id+name paired (notify channel modal must not zip separate lists).
            const participantsWithNames = participantIds.map((id) => {
              const participantMessage = this.messages.find(m =>
                (m.sender_id == id && m.sender_name) || (m.recipient_id == id && m.recipient_name)
              );
              let name = `User ${id}`;
              if (participantMessage) {
                name = participantMessage.sender_id == id
                  ? participantMessage.sender_name
                  : participantMessage.recipient_name;
              }
              return { id, name };
            });
            const otherParticipants = participantsWithNames.filter(
              (p) => String(p.id) !== String(currentUser)
            );
            const participantNames = participantsWithNames
              .map((p) => p.name)
              .filter((name, index, arr) => arr.indexOf(name) === index);
            const otherParticipantNames = otherParticipants.map((p) => p.name);

            // Create display name from other participants
            let displayName = otherParticipantNames.join(', ');
            
            // Truncate if too long (more than 30 characters)
            if (displayName.length > 30) {
              displayName = displayName.substring(0, 27) + '...';
            }
            
            // Fallback if no other participants found
            if (!displayName || displayName.trim() === '') {
              displayName = `Group Chat (${participantNames.length} members)`;
            }
            
            threads.set(threadKey, {
              id: threadKey,
              isGroup: true,
              groupId: message.group_id,
              participants: participantIds,
              participantNames: participantNames,
              otherParticipants: otherParticipants,
              otherParticipantNames: otherParticipantNames, // For the expandable list
              messages: [],
              lastMessage: null,
              unreadCount: 0,
              otherParticipant: displayName,
              otherParticipantId: null,
              patientFocusId: null,
              patientFocusName: null,
              seenMessages: new Set() // Track unique messages by group_id + msgTimestamp
            });
          }
          
          const thread = threads.get(threadKey);
          
          // Deduplicate group messages: only add if we haven't seen this exact message before
          const messageKey = `${message.group_id}_${message.msgTimestamp}_${message.message}`;
          if (!thread.seenMessages.has(messageKey)) {
            thread.seenMessages.add(messageKey);
            thread.messages.push(message);
            
            // Update last message
            const messageTime = new Date(message.msgTimestamp || message.msgDate);
            const lastMessageTime = thread.lastMessage ? new Date(thread.lastMessage.msgTimestamp || thread.lastMessage.msgDate) : null;
            
            if (!thread.lastMessage || messageTime > lastMessageTime) {
              thread.lastMessage = message;
            }
          }
          
          // Count unread messages for group chats (only if current user is recipient)
          if (message.recipient_id == currentUser && message.msgStatus === 'NEW') {
            thread.unreadCount++;
          }
          
        } else {
          // Handle individual messages (existing logic)
          let otherParticipantId, otherParticipantName;
          
          if (message.sender_id == currentUser) {
            // Current user sent this message, other participant is the recipient
            otherParticipantId = message.recipient_id;
            otherParticipantName = message.recipient_name;
          } else if (message.recipient_id == currentUser) {
            // Current user received this message, other participant is the sender
            otherParticipantId = message.sender_id;
            otherParticipantName = message.sender_name;
          } else {
            // This shouldn't happen if our query is correct, but handle it
            console.warn(`Message doesn't involve current user: sender=${message.sender_id}, recipient=${message.recipient_id}, current=${currentUser}`);
            return; // Skip this message
          }
          
          // console.log(`Other participant: ${otherParticipantName} (${otherParticipantId})`);
          
          // Split 1:1 threads by pair + patient focus so chats about different patients stay separate.
          const focusKey = message.patient_focus_id ? String(message.patient_focus_id) : 'none';
          const participants = [currentUser, otherParticipantId].sort().join('-') + '::' + focusKey;
          
          if (!threads.has(participants)) {
            threads.set(participants, {
              id: participants,
              isGroup: false,
              participants: [currentUser, otherParticipantId],
              messages: [],
              lastMessage: null,
              unreadCount: 0,
              otherParticipant: otherParticipantName,
              otherParticipantId: otherParticipantId,
              patientFocusId: message.patient_focus_id || null,
              patientFocusName: message.patient_focus_name || null
            });
          }
          
          const thread = threads.get(participants);
          thread.messages.push(message);
          if (!thread.patientFocusName && message.patient_focus_name) {
            thread.patientFocusName = message.patient_focus_name;
          }
          
          // Update last message (most recent) using full timestamp
          const messageTime = new Date(message.msgTimestamp || message.msgDate);
          const lastMessageTime = thread.lastMessage ? new Date(thread.lastMessage.msgTimestamp || thread.lastMessage.msgDate) : null;
          
          if (!thread.lastMessage || messageTime > lastMessageTime) {
            thread.lastMessage = message;
          }
          
          // Count unread messages
          if (message.msgStatus === 'NEW') {
            thread.unreadCount++;
          }
        }
      });
      
      // Convert to array and sort by last message timestamp (newest first)
      const threadsArray = Array.from(threads.values());
      this.conversationThreads = threadsArray.sort((a, b) => {
        const dateA = new Date(a.lastMessage.msgTimestamp || a.lastMessage.msgDate);
        const dateB = new Date(b.lastMessage.msgTimestamp || b.lastMessage.msgDate);
        return dateB - dateA; // Newest first
      });
      
      // Debug: Log unread counts for each thread
      // console.log('--- Thread Unread Counts ---');
      // this.conversationThreads.forEach(thread => {
      //   console.log(`${thread.otherParticipant}: ${thread.unreadCount} unread messages`);
      // });
      // console.log(`Total unread count: ${this.unreadCount}`);
      // console.log('---');
      
      this.filteredThreads = [...this.conversationThreads];
    },
    
    sortConversationThreads() {
      // Debug: Log thread dates before sorting
      console.log('--- Thread Sorting Debug ---');
      this.conversationThreads.forEach(thread => {
        const rawTimestamp = thread.lastMessage.msgTimestamp;
        const rawDate = thread.lastMessage.msgDate;
        const parsedTimestamp = new Date(rawTimestamp || rawDate);
        
        console.log(`Thread: ${thread.otherParticipant}`);
        console.log(`  Raw msgTimestamp: ${rawTimestamp}`);
        console.log(`  Raw msgDate: ${rawDate}`);
        console.log(`  Parsed Date: ${parsedTimestamp}`);
        console.log(`  Is Valid Date: ${!isNaN(parsedTimestamp.getTime())}`);
        console.log('---');
      });
      
      // Re-sort conversation threads by last message timestamp (newest first)
      this.conversationThreads.sort((a, b) => {
        const dateA = new Date(a.lastMessage.msgTimestamp || a.lastMessage.msgDate);
        const dateB = new Date(b.lastMessage.msgTimestamp || b.lastMessage.msgDate);
        
        console.log(`Comparing: ${a.otherParticipant} (${dateA}) vs ${b.otherParticipant} (${dateB})`);
        console.log(`Result: ${dateB - dateA}`);
        
        return dateB - dateA;
      });
      
      // Update filtered threads to maintain the sort order
      if (!this.searchTerm.trim()) {
        this.filteredThreads = [...this.conversationThreads];
      }
      
      console.log('--- Final Sort Order ---');
      this.conversationThreads.forEach((thread, index) => {
        const date = new Date(thread.lastMessage.msgTimestamp || thread.lastMessage.msgDate);
        console.log(`${index + 1}. ${thread.otherParticipant} - ${date}`);
      });
    },
    
    searchMessages() {
      if (!this.searchTerm.trim()) {
        this.filteredThreads = [...this.conversationThreads];
        return;
      }
      
      const term = this.searchTerm.toLowerCase();
      this.filteredThreads = this.conversationThreads.filter(thread => 
        // Search in participant name
        (thread.otherParticipant && thread.otherParticipant.toLowerCase().includes(term)) ||
        (thread.patientFocusName && thread.patientFocusName.toLowerCase().includes(term)) ||
        // Search in any message content within the thread
        thread.messages.some(message => 
          (message.subject && message.subject.toLowerCase().includes(term)) ||
          (message.message && message.message.toLowerCase().includes(term))
        )
      );
    },
    
    async openThread(thread) {
      if (this.viewChangeBlocked()) return;
      this.selectedThread = thread;
      this.activeView = 'thread';
      
      // Collect all unread message IDs in this thread
      const unreadMessageIds = thread.messages
        .filter(message => message.msgStatus === 'NEW')
        .map(message => message.pd);
      
      // console.log('Opening thread:', thread.otherParticipant);
      // console.log('Thread unread count before:', thread.unreadCount);
      // console.log('Unread message IDs:', unreadMessageIds);
      // console.log('Total unread count before:', this.unreadCount);
      
      // Mark messages as read in database first
      if (unreadMessageIds.length > 0) {
        // console.log(`Marking ${unreadMessageIds.length} messages as read:`, unreadMessageIds);
        const success = await this.markMessagesAsRead(unreadMessageIds);
        
        if (success) {
          // Only update frontend state after successful API call
          thread.messages.forEach(message => {
            if (message.msgStatus === 'NEW') {
              message.msgStatus = 'read'; // Use lowercase to match database
            }
          });
          
          // Reset unread count
          thread.unreadCount = 0;
          
          // Re-sort conversation list since unread count changed
          this.sortConversationThreads();
          
          // console.log('Thread unread count after:', thread.unreadCount);
          // console.log('Total unread count after:', this.unreadCount);
          
          // Emit global event to update notification count
          this.$vueEventBus.$emit('update-notification-count', this.unreadCount);
        }
      }
      
      // Scroll to bottom after DOM update
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    
    openMessage(message) {
      if (this.viewChangeBlocked()) return;
      this.selectedMessage = message;
      this.activeView = 'detail';
      
      // Mark as read if it was unread
      if (message.msgStatus === 'NEW') {
        message.msgStatus = 'read'; // Use lowercase to match database
      }
    },
    
    backToInbox() {
      if (this.viewChangeBlocked()) return;
      this.activeView = 'inbox';
      this.isReplying = false;
      this.replyMessage = '';
    },
    
    // Contact Search
    async searchContacts() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      this.searchTimeout = setTimeout(async () => {
        if (!this.newMessage.searchTerm.trim()) {
          this.searchResults = [];
          return;
        }
        
        try {
          const content = {
            Messages: {
              API_Meth: "SearchContacts",
              accountnumber: this.messagingIdentity.accountNumber,
              searchterm: this.newMessage.searchTerm,
              searchtype: "SearchContacts",
              sbaccnt: this.messagingIdentity.subAccountNumber,
            },
          };
          
          const response = await axios.post(CURR_API + "/tswebhook.php", content, {
            headers: { "Content-Type": "application/json;" },
          });
          
          if (response.data.status === "Successfull") {
            this.searchResults = response.data.records || [];
          }
        } catch (error) {
          console.error('Error searching contacts:', error);
          this.searchResults = [];
        }
      }, 300);
    },
    
    addRecipient(contact) {
      const recipient = {
        name: `${contact.Firstname} ${contact.Lastname}`,
        email: contact.email,
        subaccountnumber: contact.subaccountnumber
      };
      
      // Check if already added
      const exists = this.selectedRecipients.find(r => r.subaccountnumber === recipient.subaccountnumber);
      if (!exists) {
        this.selectedRecipients.push(recipient);
      }
      
      this.newMessage.searchTerm = '';
      this.searchResults = [];
    },
    
    removeRecipient(index) {
      this.selectedRecipients.splice(index, 1);
    },

    // Load patients both sender + recipients are assigned to (compose dropdown).
    async refreshSharedPatients() {
      this.selectedPatientFocusId = '';
      this.sharedPatients = [];

      if (!this.canSelectPatientFocus) {
        return;
      }

      const recipientIds = this.selectedRecipients
        .map((r) => String(r.subaccountnumber || r.id || '').trim())
        .filter(Boolean);

      if (!recipientIds.length) return;

      this.sharedPatientsLoading = true;
      try {
        const content = {
          Messages: {
            API_Meth: "GetSharedPatientsForMessaging",
            accountnumber: this.messagingIdentity.accountNumber,
            requester_unique_ID: this.messagingIdentity.subAccountNumber,
            other_unique_IDs: recipientIds,
          },
        };
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        if (response.data && String(response.data.status).toLowerCase() === "successful") {
          this.sharedPatients = Array.isArray(response.data.data) ? response.data.data : [];
        } else {
          this.sharedPatients = [];
        }
      } catch (error) {
        console.error('Error loading shared patients:', error);
        this.sharedPatients = [];
      } finally {
        this.sharedPatientsLoading = false;
      }
    },
    
    // New contact selection methods
    openContactSelection() {
      console.log('Opening contact selection...');
      console.log('Current user data:', this.allData);
      this.showContactSelection = true;
    },
    
    onPatientGroupSelected(groupData) {
      // Handle patient group selection - automatically create group chat
      console.log('Patient group selected:', groupData);
      
      // Clear existing recipients
      this.selectedRecipients = [];
      this.selectedPatientFocusId = '';
      this.sharedPatients = [];
      
      // Add all group participants as recipients
      groupData.participants.forEach(participant => {
        this.selectedRecipients.push({
          id: participant.id,
          name: participant.name,
          email: participant.email,
          accountType: participant.accountType,
          subaccountnumber: participant.id
        });
      });
      
      // Set group message mode and use group name as subject
      this.sendSeparately = false; // Force group messaging
      this.subject = groupData.groupName;
      
      // Close contact selection
      this.showContactSelection = false;
      
      // Focus on message input
      this.$nextTick(() => {
        const messageInput = this.$refs.messageTextarea;
        if (messageInput) {
          messageInput.focus();
        }
      });
    },

    onContactsSelected(contacts) {
      const incoming = Array.isArray(contacts) ? contacts : [];
      const normalized = incoming.map((contact) => {
        const stableId =
          contact.subAccountNumber ||
          contact.subaccountnumber ||
          contact.id ||
          contact.unique_ID ||
          contact.email ||
          `${contact.name || 'recipient'}-${Math.random()}`;
        return {
          id: String(stableId),
          name: contact.name || "",
          email: contact.email || "",
          subaccountnumber: String(stableId),
          accountType: contact.accountType || "",
          isExternal: !!contact.isExternal,
        };
      });

      // Keep exactly what the modal selected, with dedupe by id+name.
      const unique = [];
      const seen = new Set();
      normalized.forEach((recipient) => {
        const key = `${recipient.id}::${recipient.name}`;
        if (seen.has(key)) return;
        seen.add(key);
        unique.push(recipient);
      });
      this.selectedRecipients = unique;
      this.showContactSelection = false;
    },
    
    clearAllRecipients() {
      this.selectedRecipients = [];
    },
    
    getRecipientIcon(recipient) {
      const iconMap = {
        'administrator': ['fas', 'user-md'],
        'nurse-manager': ['fas', 'user-md'],
        'provider': ['fas', 'user-md'],
        'registered-nurse': ['fas', 'user-md'],
        'licensed-practical-nurse': ['fas', 'user-md'],
        'home-health-aide': ['fas', 'user-md'],
        'nursing-agency': ['fas', 'building'],
        'home-healthcare-agency': ['fas', 'building'],
        'care-management-agency': ['fas', 'building'],
        'patients': ['fas', 'user-md'],
        'external': ['fas', 'envelope']
      };
      
      return iconMap[recipient.accountType] || ['fas', 'user-md'];
    },
    
    getRecipientRole(recipient) {
      if (recipient.isExternal) return 'External Contact';
      
      const roleMap = {
        'administrator': 'Administrator',
        'nurse-manager': 'Nurse Manager',
        'provider': 'Provider',
        'registered-nurse': 'Registered Nurse',
        'licensed-practical-nurse': 'Licensed Practical Nurse',
        'home-health-aide': 'Home Health Aide',
        'nursing-agency': 'Nursing Agency',
        'home-healthcare-agency': 'Home Healthcare Agency',
        'care-management-agency': 'Care Management Agency',
        'patients': 'Patient'
      };
      
      return roleMap[recipient.accountType] || recipient.accountType?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Contact';
    },
    
    // Message Sending
    async sendMessage() {
      // Not while a video is still going up, and not twice at once.
      if (this.uploadsBusy('compose') || !this.beginSend('compose')) return;
      try {
        if (this.sendSeparately || this.selectedRecipients.length === 1) {
          // Send separately to each recipient (current behavior)
          await this.sendIndividualMessages();
        } else {
          // Create group chat (new behavior)
          await this.sendGroupMessage();
        }
      } catch (error) {
        console.error('Error sending message:', error);
        // A refusal answered with an error status (413 for an oversized file,
        // 503 while uploads are paused) arrives here rather than as a response.
        alert(refusalMessage(error?.response?.data) || "Error sending message. Please try again.");
      } finally {
        this.endSend('compose');
      }
    },

    // Send individual messages to each recipient (existing behavior)
    async sendIndividualMessages() {
      console.log('Sending individual messages to:', this.selectedRecipients);
      
      // Send one message per recipient (separate conversations)
      let successCount = 0;
      // The first refusal in the server's words. Every recipient gets the same
      // attachment, so a paused or oversized upload refuses them all alike.
      let refusal = null;
      // A video is uploaded once and named by uid in each message. If a message
      // goes out without it (an older replica ignored the field), the rest are
      // not sent into the same server, and those that still need it stay selected.
      const videoUids = this.composeVideoUids();
      const completed = new Set();
      let videoMissing = false;
      
      for (const recipient of this.selectedRecipients) {
        const formData = new FormData();
        formData.append("API_Meth", "SendInternalMessages");
        
        // Send to ONE recipient at a time
        formData.append("recieversubaccountnumber", recipient.subaccountnumber);
        formData.append("sendersubaccountnumber", this.messagingIdentity.subAccountNumber);
        formData.append("senderaccountnunber", this.messagingIdentity.accountNumber);
        formData.append("additionalcontacts", recipient.email || '');
        formData.append("internalcontacts", recipient.subaccountnumber);
        formData.append("message", this.composeMessageBody());
        formData.append("subject", this.composedSubject);
        formData.append("status", "NEW");
        formData.append("senderName", this.messagingIdentity.senderName);
        this.appendComposeAttachment(formData);
        // Stamp patient focus on the row so inbox can group by it later.
        if (this.canSelectPatientFocus && this.selectedPatientFocusId) {
          formData.append("patient_focus_id", this.selectedPatientFocusId);
        }
        if (this.notifyRecipients) {
          formData.append("notify_recipients", "1");
          this.appendNotifyChannels(formData, [recipient]);
        }
        
        try {
          const response = await axios.post(CURR_API + "/tswebhook.php", formData);
          if (response.data.message === "Inserted") {
            if (!this.verifyVideoAttached(response.data, videoUids)) {
              videoMissing = true;
              break;
            }
            successCount++;
            completed.add(recipient);
            console.log(`✅ Message sent to ${recipient.name}`);
          } else {
            refusal = refusal || refusalMessage(response.data);
            console.error(`❌ Failed to send to ${recipient.name}:`, response.data);
          }
        } catch (error) {
          refusal = refusal || refusalMessage(error?.response?.data);
          console.error(`❌ Error sending to ${recipient.name}:`, error);
        }
      }
      
      if (videoMissing) {
        // The composer, its text and the uploaded video stay for a second Send.
        this.selectedRecipients = this.selectedRecipients.filter((recipient) => !completed.has(recipient));
        return;
      }
      if (successCount > 0) {
        alert(`Message sent separately to ${successCount}/${this.selectedRecipients.length} recipient${this.selectedRecipients.length !== 1 ? 's' : ''}!`);
        this.clearMessage();
        this.setActiveView('inbox');
        await this.fetchMessages();
        this.sortConversationThreads();
      } else {
        alert(refusal || "Error sending messages. Please try again.");
      }
    },

    // Send group message (new behavior)
    async sendGroupMessage() {
      console.log('Sending group message to:', this.selectedRecipients);
      
      const formData = new FormData();
      formData.append("API_Meth", "SendGroupMessage");
      
      // Prepare participants list (ONLY recipients, NOT sender)
      const recipientIds = this.selectedRecipients.map(r => r.subaccountnumber);
      const senderSubaccnt = this.messagingIdentity.subAccountNumber;
      
      // For the database, we include sender in participants list for grouping
      // But we only send messages to recipients, not to sender
      const allParticipants = [senderSubaccnt, ...recipientIds];
      
      formData.append("sendersubaccountnumber", senderSubaccnt);
      formData.append("senderaccountnunber", this.messagingIdentity.accountNumber);
      formData.append("participants", recipientIds.join(',')); // Only send to recipients
      formData.append("all_participants", allParticipants.join(',')); // For grouping purposes
      formData.append("message", this.composeMessageBody());
      formData.append("subject", this.newMessage.subject);
      formData.append("status", "NEW");
      formData.append("senderName", this.messagingIdentity.senderName);
      this.appendComposeAttachment(formData);
      if (this.notifyRecipients) {
        formData.append("notify_recipients", "1");
        this.appendNotifyChannels(formData);
      }
      
      const response = await axios.post(CURR_API + "/tswebhook.php", formData);
      
      if (response.data.message === "Inserted") {
        // Without the video on the message the composer stays as it is.
        if (!this.verifyVideoAttached(response.data, this.composeVideoUids())) return;
        const participantCount = response.data.participants_notified || this.selectedRecipients.length;
        alert(`Group chat created with ${participantCount} recipient${participantCount !== 1 ? 's' : ''}!`);
        this.clearMessage();
        this.setActiveView('inbox');
        await this.fetchMessages();
        this.sortConversationThreads();
      } else {
        alert(refusalMessage(response.data) || "Error creating group chat. Please try again.");
        console.error('Group message error:', response.data);
      }
    },
    
    clearMessage() {
      this.newMessage = {
        searchTerm: '',
        subject: '',
        message: ''
      };
      this.selectedRecipients = [];
      this.sendSeparately = false; // Reset to default
      this.notifyRecipients = false;
      this.resetNotifyChannelState();
      this.searchResults = [];
      this.showContactSelection = false;
      this.sharedPatients = [];
      this.selectedPatientFocusId = '';
      this.sharedPatientsLoading = false;
      this.clearAllAttachments();
    },
    
    // Reply Functions
    replyToMessage() {
      this.isReplying = true;
      this.replyMessage = '';
    },
    
    cancelReply() {
      this.isReplying = false;
      this.replyMessage = '';
      this.clearReplyAttachment();
    },
    
    async sendReply() {
      if (!this.canSendReply || !this.beginSend('reply')) return;
      try {
        const formData = new FormData();
        formData.append("API_Meth", "SendInternalMessages");
        
        formData.append("recieversubaccountnumber", this.selectedMessage.subaccnt_Id);
        formData.append("sendersubaccountnumber", this.messagingIdentity.subAccountNumber);
        formData.append("senderaccountnunber", this.messagingIdentity.accountNumber);
        formData.append("additionalcontacts", '');
        formData.append("internalcontacts", '');
        formData.append("message", this.replyMessageBody());
        formData.append("subject", this.replySubject(this.selectedMessage.subject));
        formData.append("status", "NEW");
        formData.append("senderName", this.messagingIdentity.senderName);
        this.appendReplyAttachment(formData);
        // Replies keep the same patient focus — no changing mid-thread.
        if (this.selectedMessage.patient_focus_id) {
          formData.append("patient_focus_id", this.selectedMessage.patient_focus_id);
        }
        if (this.notifyRecipients) {
          formData.append("notify_recipients", "1");
        }
        
        const response = await axios.post(CURR_API + "/tswebhook.php", formData);
        
        if (response.data.message === "Inserted") {
          if (!this.verifyVideoAttached(response.data, this.replyVideoUids())) return;
          alert("Reply sent successfully!");
          this.cancelReply();
          this.notifyRecipients = false;
          await this.fetchMessages();
          // Re-sort conversation list since we sent a reply
          this.sortConversationThreads();
        } else {
          alert(refusalMessage(response.data) || "Error sending reply. Please try again.");
        }
      } catch (error) {
        console.error('Error sending reply:', error);
        alert(refusalMessage(error?.response?.data) || "Error sending reply. Please try again.");
      } finally {
        this.endSend('reply');
      }
    },
    
    // Utility Functions — msgTimestamp (UTC) → local day label + time.
    formatDate(messageOrRaw) {
      return formatMessageWhen(messageOrRaw);
    },
    
    async sendQuickReply() {
      // Enter in the box calls this too: a second Enter while the request is out is the same message.
      if (!this.canSendQuickReply || !this.selectedThread || !this.beginSend('quickReply')) return;
      
      console.log('Sending quick reply to thread:', this.selectedThread);
      
      try {
        const formData = new FormData();
        
        // Handle group chat replies vs individual replies
        if (this.selectedThread.isGroup) {
          console.log('Sending group chat reply to existing group:', this.selectedThread.groupId);
          
          // Use group message API
          formData.append("API_Meth", "SendGroupMessage");
          
          // Get all participants except sender for group reply
          const recipientIds = this.selectedThread.participants.filter(id => id != this.messagingIdentity.subAccountNumber);
          const allParticipants = this.selectedThread.participants;
          
          formData.append("sendersubaccountnumber", this.messagingIdentity.subAccountNumber);
          formData.append("senderaccountnunber", this.messagingIdentity.accountNumber);
          formData.append("participants", recipientIds.join(','));
          formData.append("all_participants", allParticipants.join(','));
          formData.append("group_id", this.selectedThread.groupId); // CRUCIAL: Reuse existing group ID
          formData.append("message", this.quickReplyMessageBody());
          formData.append("subject", this.replySubject(this.selectedThread.lastMessage?.subject, 'Group Message'));
          formData.append("status", "NEW");
          formData.append("senderName", this.messagingIdentity.senderName);
          this.appendQuickReplyAttachment(formData);
          
        } else {
          console.log('Sending individual reply');
          
          // Use individual message API
          formData.append("API_Meth", "SendInternalMessages");
          formData.append("recieversubaccountnumber", this.selectedThread.otherParticipantId);
          formData.append("sendersubaccountnumber", this.messagingIdentity.subAccountNumber);
          formData.append("senderaccountnunber", this.messagingIdentity.accountNumber);
          formData.append("additionalcontacts", '');
          formData.append("internalcontacts", this.selectedThread.otherParticipantId);
          formData.append("message", this.quickReplyMessageBody());
          formData.append("subject", this.replySubject(this.selectedThread.lastMessage?.subject));
          formData.append("status", "NEW");
          formData.append("senderName", this.messagingIdentity.senderName);
          this.appendQuickReplyAttachment(formData);
          // Keep thread's patient focus on quick replies too.
          if (this.selectedThread.patientFocusId) {
            formData.append("patient_focus_id", this.selectedThread.patientFocusId);
          }
        }
        if (this.notifyRecipients) {
          formData.append("notify_recipients", "1");
          this.appendNotifyChannels(formData);
        }
        
        const response = await axios.post(CURR_API + "/tswebhook.php", formData);
        
        console.log('Quick reply response:', response.data);
        
        if (response.data && (response.data.message === "Inserted" || response.data.status === "200-Successfull")) {
          if (!this.verifyVideoAttached(response.data, this.quickReplyVideoUids())) return;
          if (response.data.upload_warnings && response.data.upload_warnings.length) {
            console.warn("Attachment upload warnings:", response.data.upload_warnings);
            alert("Message sent, but the attachment may not have uploaded. Check the console for details.");
          }
          // Clear the reply input
          this.quickReplyMessage = '';
          this.clearQuickReplyAttachment();
          this.notifyRecipients = false;
          this.resetNotifyChannelState();
          
          // Refresh messages to show the new reply
          await this.fetchMessages();
          
          // Find and reselect the current thread
          const updatedThread = this.conversationThreads.find(t => t.id === this.selectedThread.id);
          if (updatedThread) {
            this.selectedThread = updatedThread;
            
            // Re-sort conversation list since we added a new message (lastMessage changed)
            this.sortConversationThreads();
            
            // Scroll to bottom to show the new message
            this.$nextTick(() => {
              this.scrollToBottom();
            });
          }
        } else {
          alert(refusalMessage(response.data) || "Error sending message. Please try again.");
        }
      } catch (error) {
        console.error('Error sending quick reply:', error);
        alert(refusalMessage(error?.response?.data) || "Error sending message. Please try again.");
      } finally {
        this.endSend('quickReply');
      }
    },
    
    getParticipantName(participantId) {
      // Helper method to get participant name from ID
      // For now, return the ID - this could be enhanced to lookup actual names
      return `User ${participantId}`;
    },
    
    getSenderName(message) {
      // Get sender name for group messages
      if (message.sender_name) {
        return message.sender_name;
      }
      // Fallback to participant ID if name not available
      return `User ${message.sender_id}`;
    },
    
    async markMessagesAsRead(messageIds) {
      try {
        const formData = new FormData();
        formData.append("API_Meth", "MarkMessagesAsRead");
        formData.append("message_ids", messageIds.join(','));
        formData.append("user_id", this.messagingIdentity.subAccountNumber);
        formData.append("account_id", this.messagingIdentity.accountNumber);
        
        const response = await axios.post(CURR_API + "/tswebhook.php", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        
        console.log('Mark as read response:', response.data);
        
        if (response.data && response.data.status === "success") {
          console.log(`Successfully marked ${response.data.updated_count} messages as read`);
          return true;
        } else {
          throw new Error(response.data?.message || 'Unknown error marking messages as read');
        }
      } catch (error) {
        console.error('Error marking messages as read:', error);
        alert(`Error marking messages as read: ${error.message}`);
        return false;
      }
    },
    
    // getRecipientName method is no longer needed since we now get 
    // sender_name and recipient_name directly from the database JOIN
    
    scrollToBottom() {
      if (this.$refs.chatMessages) {
        this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
      }
    },
    
    truncateMessage(message, length = 50) {
      const text = String(message || "");
      if (!text || text === "(Attachment)") return "";
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },

    threadPreviewText(thread) {
      const msg = thread?.lastMessage;
      const text = this.truncateMessage(msg?.message);
      if (text) return text;
      // Attachment-only messages: show kind/count instead of blank preview.
      if (this.hasMessageAttachment(msg)) {
        return this.attachmentPreviewLabel(msg);
      }
      return "";
    }
  }
};
</script>

<style scoped>
.floating-message-widget {
  position: fixed;
  /* Clear FooterBar (1.5rem pad ×2 + ~3 stacked 0.8rem links) plus a small gap */
  bottom: 7.5rem;
  right: 16px;
  z-index: 10000;
  font-family: "Inter", system-ui, sans-serif;
}

/* Collapsed Message Bar — brighter teal so it reads against the deep-teal footer */
.message-bar {
  background: #4b9ca5;
  color: white;
  padding: 10px 18px;
  border-radius: 6px;
  border: 1px solid #2f6f76;
  cursor: pointer;
  box-shadow: none;
  transition: background 0.15s ease;
  min-width: 0;
}

.message-bar:hover {
  transform: none;
  background: #3d8790;
  box-shadow: none;
}

.message-bar.has-unread {
  border: 2px solid #cb5f50;
}

.message-bar-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.chat-icon {
  font-size: 16px;
}

.message-text {
  font-weight: 600;
  font-size: 15px;
  font-variant-caps: all-small-caps;
  letter-spacing: 0.03em;
}

.unread-badge {
  background: #cb5f50;
  color: white;
  border-radius: 4px;
  padding: 2px 7px;
  font-size: 12px;
  font-weight: 700;
  margin-left: auto;
}

/* Expanded Widget */
.message-widget.expanded {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  width: 480px;
  height: 700px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideUp 0.3s ease;
  position: relative;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Widget Header */
.widget-header {
  background: #24555b;
  color: white;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 16px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.minimize-btn,
.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.minimize-btn:hover,
.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Widget Navigation */
.widget-nav {
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.nav-btn {
  flex: 1;
  background: none;
  border: none;
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  color: #6c757d;
  transition: all 0.2s ease;
  position: relative;
}

.nav-btn:hover {
  background: #e9ecef;
  color: #495057;
}

.nav-btn.active {
  background: white;
  color: #4b9ca5;
  border-bottom: 2px solid #4b9ca5;
}

.nav-badge {
  background: #ff4757;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: bold;
  margin-left: 4px;
}

/* Widget Content */
.widget-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Inbox View */
.inbox-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Search Bar */
.search-bar {
  padding: 16px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 10px;
  background: white;
}

.search-icon {
  color: #6c757d;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #495057;
}

.search-input::placeholder {
  color: #adb5bd;
}

/* Message List */
.message-list {
  flex: 1;
  overflow-y: auto;
  background: #f8f9fa;
}

/* Thread Items */
.thread-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
  transition: background-color 0.2s;
}

.thread-item:hover {
  background: #ffffff;
}

.thread-item.unread {
  background: #f8f9fa;
  border-left: 3px solid #007bff;
}

.thread-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.thread-avatar .fa-icon {
  color: #6c757d;
  font-size: 18px;
}

.thread-content {
  flex: 1;
  min-width: 0;
}

.thread-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.participant-name {
  font-weight: 600;
  color: #212529;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.group-icon {
  color: #3498db;
  font-size: 12px;
}

.thread-date {
  font-size: 12px;
  color: #6c757d;
}

.thread-preview {
  color: #495057;
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
  min-width: 0;
}

.thread-preview-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hidden-file-input {
  display: none;
}

.attachment-compose-row,
.quick-reply-attachment,
.attachment-compose-list,
.message-attachments-list,
.thread-attachment-thumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.attachment-compose-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.attach-btn,
.clear-attach-btn,
.quick-attach-btn {
  border: 1px solid #ced4da;
  background: #fff;
  color: #495057;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  cursor: pointer;
}

.quick-attach-btn {
  padding: 8px 10px;
  flex-shrink: 0;
}

.attach-btn:hover,
.clear-attach-btn:hover,
.quick-attach-btn:hover {
  background: #f8f9fa;
}

.attachment-form-group {
  margin-top: 4px;
}

.quick-reply-attachment {
  margin-top: 8px;
}

.thread-patient-focus {
  color: #4c5fd7;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 2px;
}

.thread-patient-focus-header {
  color: #4c5fd7;
  font-size: 12px;
  font-weight: 600;
  margin-top: 2px;
}

.optional-label {
  font-weight: 400;
  color: #888;
  font-size: 12px;
}

.patient-focus-select {
  width: 100%;
}

.patient-focus-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #888;
}

.subject-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.patient-focus-prefix {
  flex-shrink: 0;
  background: #eef1ff;
  color: #4c5fd7;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.subject-input {
  flex: 1;
  min-width: 0;
}

.thread-info {
  font-size: 12px;
  color: #6c757d;
}

.message-count {
  font-style: italic;
}

.unread-badge {
  background: #007bff;
  color: white;
  border-radius: 12px;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: bold;
  min-width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.message-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
  transition: background 0.2s ease;
  position: relative;
  background: white;
  margin-bottom: 1px;
}

.message-item:hover {
  background: #f1f3f4;
}

.message-item.unread {
  background: #f8f9ff;
  border-left: 3px solid #4b9ca5;
}

.message-avatar {
  color: #6c757d;
  font-size: 24px;
  margin-top: 2px;
}

.message-content {
  flex: 1;
  min-width: 0;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.sender-name {
  font-weight: 600;
  color: #495057;
  font-size: 14px;
}

.message-date {
  font-size: 12px;
  color: #6c757d;
}

.message-subject {
  font-weight: 500;
  color: #495057;
  font-size: 13px;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-preview {
  font-size: 12px;
  color: #6c757d;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.unread-indicator {
  width: 8px;
  height: 8px;
  background: #4b9ca5;
  border-radius: 50%;
  position: absolute;
  right: 16px;
  top: 20px;
}

/* Empty State */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  text-align: center;
  padding: 40px 20px;
}

.empty-icon {
  font-size: 48px;
  color: #dee2e6;
  margin-bottom: 16px;
}

/* Compose View */
.compose-view {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  position: relative;
}

/* Contact Selection Overlay */
.contact-selection-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.contact-selection-overlay > * {
  max-width: 100%;
  max-height: 100%;
}

.compose-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-weight: 500;
  color: #495057;
  font-size: 14px;
}

/* Group Messaging Options */
.messaging-options {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  margin-bottom: 8px;
}

.messaging-checkbox {
  margin-right: 8px;
  accent-color: #007bff;
}

.checkbox-text {
  font-weight: 500;
  color: #495057;
}

.option-description {
  margin-top: 8px;
  margin-left: 24px;
}

.select-channels-btn {
  display: inline-flex;
  align-items: center;
  margin-top: 10px;
  margin-left: 24px;
  background: #24555b;
  color: white;
  border: none;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  align-self: flex-start;
}

.select-channels-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(36, 85, 91, 0.35);
}

.option-desc {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #6c757d;
  font-style: italic;
}

.desc-icon {
  margin-right: 6px;
  color: #007bff;
}

.form-input,
.form-textarea {
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 14px;
  color: #495057;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #4b9ca5;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.form-file {
  border: 1px dashed #ced4da;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.form-file:hover {
  border-color: #4b9ca5;
}

/* New Recipient Selection */
.recipient-selection {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.add-recipients-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #24555b;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  align-self: flex-start;
}

.add-recipients-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.recipients-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #495057;
  font-weight: 500;
}

.clear-all-recipients {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.clear-all-recipients:hover {
  background: rgba(220, 53, 69, 0.1);
}

.recipients-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.recipient-tag {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.recipient-tag:hover {
  background: #e9ecef;
  border-color: #ced4da;
}

.recipient-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #4b9ca5;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.recipient-name {
  font-weight: 500;
  color: #495057;
  flex: 1;
}

.recipient-role {
  font-size: 12px;
  color: #6c757d;
  background: #e9ecef;
  padding: 2px 6px;
  border-radius: 4px;
}

.remove-recipient {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s ease;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-recipient:hover {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.no-recipients {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6c757d;
  font-size: 14px;
  padding: 16px;
  border: 2px dashed #e9ecef;
  border-radius: 8px;
  text-align: center;
  justify-content: center;
}

.no-recipients-icon {
  font-size: 16px;
  opacity: 0.7;
}

/* Legacy recipient input (keeping for compatibility) */
.recipient-input {
  position: relative;
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ced4da;
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
}

.search-result-item {
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  transition: background 0.2s ease;
}

.search-result-item:hover {
  background: #f8f9fa;
}

.search-result-item:last-child {
  border-bottom: none;
}

/* Selected Recipients */
.selected-recipients {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.recipient-tag {
  background: #e7f3ff;
  color: #0066cc;
  padding: 4px 8px;
  border-radius: 16px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.remove-recipient {
  background: none;
  border: none;
  color: #0066cc;
  cursor: pointer;
  padding: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s ease;
}

.remove-recipient:hover {
  background: rgba(0, 102, 204, 0.1);
}

/* File Info */
.file-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 14px;
  color: #495057;
}

.remove-file {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  transition: color 0.2s ease;
}

.remove-file:hover {
  color: #dc3545;
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 10px;
  border-top: 1px solid #e9ecef;
}

.send-btn {
  background: #24555b;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.send-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.send-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.clear-btn,
.cancel-btn {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #ced4da;
  padding: 10px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.clear-btn:hover,
.cancel-btn:hover {
  background: #e9ecef;
}

/* Message Detail View */
.message-detail-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
  background: white;
}

.back-btn,
.reply-btn {
  background: none;
  border: 1px solid #ced4da;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #495057;
  transition: all 0.2s ease;
}

.back-btn:hover,
.reply-btn:hover {
  background: #f8f9fa;
  border-color: #adb5bd;
}

.reply-btn {
  background: #4b9ca5;
  color: white;
  border-color: #4b9ca5;
}

.reply-btn:hover {
  background: #5a6fd8;
  border-color: #5a6fd8;
}

.message-detail {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.detail-info h3 {
  margin: 0 0 12px 0;
  color: #495057;
  font-size: 18px;
  font-weight: 600;
}

.message-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #6c757d;
}

.message-body {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: #495057;
  margin-bottom: 20px;
  white-space: pre-wrap;
}

/* Reply Form */
.reply-form {
  border-top: 1px solid #e9ecef;
  padding-top: 20px;
}

.reply-form h4 {
  margin: 0 0 16px 0;
  color: #495057;
  font-size: 16px;
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 600px) {
  .message-widget.expanded {
    width: calc(100vw - 40px);
    height: calc(100vh - 40px);
    bottom: 20px;
    right: 20px;
  }
  
  .message-bar {
    min-width: 140px;
    padding: 12px 18px;
  }
  
  .compose-view,
  .message-detail {
    padding: 16px;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .send-btn {
    width: 100%;
    justify-content: center;
  }
}

/* Scrollbar Styling */
.message-list::-webkit-scrollbar,
.compose-view::-webkit-scrollbar,
.message-detail::-webkit-scrollbar {
  width: 6px;
}

.message-list::-webkit-scrollbar-track,
.compose-view::-webkit-scrollbar-track,
.message-detail::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.message-list::-webkit-scrollbar-thumb,
.compose-view::-webkit-scrollbar-thumb,
.message-detail::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.message-list::-webkit-scrollbar-thumb:hover,
.compose-view::-webkit-scrollbar-thumb:hover,
.message-detail::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Thread View Styles */
.thread-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.thread-view .thread-header {
  padding: 16px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
}

.thread-participant {
  font-weight: 600;
  color: #212529;
}

.participant-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.group-participants {
  margin-top: 8px;
}

.participants-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  background: #f8f9fa;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: #6c757d;
  transition: background-color 0.2s;
}

.participants-toggle:hover {
  background: #e9ecef;
}

.participants-count {
  font-weight: 500;
}

.participants-list {
  margin-top: 4px;
  padding: 8px;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  max-height: 150px;
  overflow-y: auto;
}

.participant-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  font-size: 13px;
}

.participant-icon {
  color: #3498db;
  font-size: 11px;
}

.participant-member-name {
  color: #495057;
}

.participant-item.current-user {
  background: #f8f9fa;
  border-radius: 3px;
  margin-bottom: 2px;
}

.participant-icon.current {
  color: #28a745;
}

.chat-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #f8f9fa;
}

.chat-message {
  margin-bottom: 16px;
  display: flex;
}

.chat-message.own-message {
  justify-content: flex-end;
}

.chat-message.other-message {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 18px;
  position: relative;
}

.own-message .message-bubble {
  background: #007bff;
  color: white;
  border-bottom-right-radius: 4px;
}

.other-message .message-bubble {
  background: white;
  color: #212529;
  border: 1px solid #e9ecef;
  border-bottom-left-radius: 4px;
}

.message-bubble .message-subject {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 4px;
  opacity: 0.9;
}

.message-bubble .message-sender {
  font-size: 11px;
  font-weight: 500;
  color: #6c757d;
  margin-bottom: 2px;
  opacity: 0.8;
}

.message-bubble .message-text {
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
}

.message-bubble .message-timestamp {
  font-size: 11px;
  margin-top: 4px;
  opacity: 0.7;
}

.quick-reply {
  padding: 16px;
  border-top: 1px solid #e9ecef;
  background: white;
}

.quick-reply-notify-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.quick-reply-notify {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0;
  font-size: 0.8rem;
}

.select-channels-btn--compact {
  margin-top: 0;
  margin-left: 0;
  padding: 6px 10px;
  font-size: 12px;
}

.reply-input-container {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.quick-reply-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.quick-reply-textarea {
  width: 100%;
  flex: 1;
  border: 1px solid #ced4da;
  border-radius: 20px;
  padding: 12px 16px;
  resize: none;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  outline: none;
  transition: border-color 0.2s;
}

.quick-reply-textarea:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.quick-send-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #007bff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  flex-shrink: 0;
}

.quick-send-btn:hover:not(:disabled) {
  background: #0056b3;
}

.quick-send-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
/* Remove, beside an attachment its sender may take back (DeleteMessageAttachment). */
.attachment-with-actions {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}
.attachment-remove {
  background: none;
  border: none;
  padding: 0;
  color: #b42318;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
}
</style>
